// =====================================================
// TREATMENTS API CLIENT
// Rutas verificadas contra treatments.controller.ts:
//   POST   /treatments                    → create
//   GET    /treatments                    → findAll (paginado con TreatmentQueryDto)
//   GET    /treatments/select             → getForSelect
//   GET    /treatments/select/site/:id    → getForSelectBySite
//   GET    /treatments/:id                → getById
//   PATCH  /treatments/:id                → update
//   DELETE /treatments/:id                → deactivate  ← DELETE, no PATCH
//   PATCH  /treatments/:id/reactivate     → reactivate
//   GET    /treatments/:id/sites          → getLinkedSites
//   PATCH  /treatments/:id/sites          → updateSites
// =====================================================
import { api } from './client';
import type { PaginatedResponse } from '../types/pagination';

// ── Tipos ─────────────────────────────────────────────

export interface TreatmentSelect {
  id: string;
  code: string;
  name: string;
}

export interface Treatment {
  id: string;
  companyId: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  sitesCount?: number;
}

export interface TreatmentLinkedSite {
  id: string;
  code: string;
  name: string;
  isLinked: boolean;
  isActive: boolean;
}

export interface TreatmentQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'code' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  includeInactive?: boolean;
}

export interface CreateTreatmentRequest {
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
}

export interface UpdateTreatmentRequest {
  code?: string;
  name?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateTreatmentSitesRequest {
  siteIds: string[];
}

// ── Cliente API ───────────────────────────────────────

export const treatmentsApi = {
  /**
   * GET /treatments/select
   * Lista para selects (solo activos)
   */
  getForSelect: async (): Promise<TreatmentSelect[]> => {
    return api.get<TreatmentSelect[]>('/treatments/select');
  },

  /**
   * GET /treatments/select/site/:siteId
   * Lista filtrada por centro
   */
  getForSelectBySite: async (siteId: string): Promise<TreatmentSelect[]> => {
    return api.get<TreatmentSelect[]>(`/treatments/select/site/${siteId}`);
  },

  /**
   * GET /treatments?page=&limit=&search=&sortBy=&sortOrder=&includeInactive=
   * El controller usa TreatmentQueryDto en GET /treatments → devuelve paginado.
   */
  getAllPaginated: async (
    params?: TreatmentQueryParams,
  ): Promise<PaginatedResponse<Treatment>> => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) qs.append(k, String(v));
      });
    }
    const query = qs.toString();
    return api.get<PaginatedResponse<Treatment>>(
      query ? `/treatments?${query}` : '/treatments',
    );
  },

  /**
   * Compatibilidad retroactiva:
   * · Con objeto params → getAllPaginated (TreatmentsAdminPage)
   * · Con boolean       → GET /treatments?includeInactive= (legado)
   */
  getAll: async (
    paramsOrIncludeInactive?: TreatmentQueryParams | boolean,
  ): Promise<any> => {
    if (typeof paramsOrIncludeInactive === 'object') {
      return treatmentsApi.getAllPaginated(paramsOrIncludeInactive);
    }
    const includeInactive = paramsOrIncludeInactive ?? false;
    const query = includeInactive ? '?includeInactive=true' : '';
    return api.get<Treatment[]>(`/treatments${query}`);
  },

  /**
   * GET /treatments/:id
   */
  getById: async (id: string): Promise<Treatment> => {
    return api.get<Treatment>(`/treatments/${id}`);
  },

  /**
   * POST /treatments
   */
  create: async (data: CreateTreatmentRequest): Promise<Treatment> => {
    return api.post<Treatment>('/treatments', data);
  },

  /**
   * PATCH /treatments/:id
   */
  update: async (id: string, data: UpdateTreatmentRequest): Promise<Treatment> => {
    return api.patch<Treatment>(`/treatments/${id}`, data);
  },

  /**
   * DELETE /treatments/:id
   * El controller usa @Delete(':id'), no @Patch(':id/deactivate')
   */
  deactivate: async (id: string): Promise<Treatment> => {
    return api.delete<Treatment>(`/treatments/${id}`);
  },

  /**
   * PATCH /treatments/:id/reactivate
   */
  reactivate: async (id: string): Promise<Treatment> => {
    return api.patch<Treatment>(`/treatments/${id}/reactivate`, {});
  },

  /**
   * GET /treatments/:id/sites
   */
  getLinkedSites: async (id: string): Promise<TreatmentLinkedSite[]> => {
    return api.get<TreatmentLinkedSite[]>(`/treatments/${id}/sites`);
  },

  /**
   * PATCH /treatments/:id/sites
   */
  updateSites: async (
    id: string,
    data: UpdateTreatmentSitesRequest,
  ): Promise<Treatment> => {
    return api.patch<Treatment>(`/treatments/${id}/sites`, data);
  },
};

export default treatmentsApi;
